import {useMessages} from "i18n";
import {ConfirmDialog, ConfirmBody} from "components/confirm/ConfirmDialog";
import {ShareCopyButton, useCopyFeedback} from "home/components/shareLink";
import {Actions, CardMain, CardText, DangerButton, GhostButton, SaveButton} from "../components/AccountCards";
import {useAccountCustomDomain} from "../hooks/useAccountCustomDomain";
import {MAX_DOMAIN_LENGTH, type CustomDomainRecord} from "./customDomain";
import {
	Badge,
	DomainField,
	DomainHeader,
	DomainInput,
	DomainName,
	ErrorText,
	FieldLabel,
	Hint,
	LapsedNote,
	RecordCaption,
	RecordCard,
	RecordField,
	RecordList,
	RecordRow,
	RecordType,
	RecordValue,
	RecordsTitle,
	Row,
	StageActions,
	StatusText,
} from "./CustomDomainSettings.styles";

/**
 * The bring-your-own custom-domain editor (kitecorp/transferify-ui#669), with NO surface of its own — the
 * caller supplies the frame and the heading, exactly as {@link SubdomainSettings} does.
 *
 * <p>Its one caller is {@link BrandSheet}, as the second section of the personalization sheet's Domain
 * tab. It sits BELOW the claimed-address editor because that is the simpler feature and the one most
 * senders want: claiming a label takes one field and no DNS, while this asks the sender to go and edit a
 * zone file.
 *
 * <p>Presentational only: the entitlement gate, the host grammar, the lifecycle mapping and the five
 * `/account/domain` calls live in {@link useAccountCustomDomain} and `customDomain.ts` (epic #26).
 *
 * <p>Three things here are contract, not styling:
 * <ol>
 * <li><b>No "Available on Premium." note of its own.</b> The sheet renders exactly one, and
 *     {@link brandSheetUpsell} decides whose it is. Both sections of this tab are gated on the same
 *     `customDomain` capability, so a second copy of that sentence would be noise for the sender and the
 *     strict-mode failure that broke both customer-branding specs when the theme section shipped one.</li>
 * <li><b>A locked sender sees no form</b>, never a disabled-looking one — the api rejects their
 *     registration anyway, so exposing the control would only produce 400s.</li>
 * <li><b>A LAPSED owner still sees their domain and the Remove button.</b> The api leaves the read and the
 *     removal ungated on every tier for exactly this reason; hiding them is the ui#534 defect, which left
 *     a host registered against every other account with nobody able to free it.</li>
 * </ol>
 */
export default function CustomDomainSettings() {
	const m = useMessages();
	const c = m.account.customDomain;
	const {state, domain, presentation, input, busy, error, confirmOpen, setInput, register, checkDns,
		rotateToken, openConfirm, closeConfirm, remove} = useAccountCustomDomain();

	return (
		<>
			<CardText>{c.description}</CardText>

			{state === "loading" && <Hint>{c.loading}</Hint>}

			{state === "empty" && (
				<CardMain>
					<Row>
						<FieldLabel htmlFor="custom-domain-host">{c.domainLabel}</FieldLabel>
					</Row>
					<Row>
						<DomainField>
							<DomainInput
								id="custom-domain-host"
								value={input}
								disabled={busy}
								maxLength={MAX_DOMAIN_LENGTH}
								placeholder={c.domainPlaceholder}
								autoCapitalize="none"
								autoCorrect="off"
								spellCheck={false}
								aria-describedby="custom-domain-hint"
								onChange={(event) => setInput(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === "Enter") {
										event.preventDefault();
										void register();
									}
								}}
							/>
						</DomainField>
						<SaveButton disabled={busy} onClick={() => void register()}>{c.add}</SaveButton>
					</Row>
					<Hint id="custom-domain-hint">{c.domainHint}</Hint>
					{error && <ErrorText role="alert">{error}</ErrorText>}
				</CardMain>
			)}

			{(state === "managed" || state === "lapsed") && domain && presentation && (
				<CardMain>
					<DomainHeader>
						<DomainName>{domain.domain}</DomainName>
						<Badge $tone={presentation.tone}>{c.badges[presentation.badge]}</Badge>
					</DomainHeader>

					{state === "lapsed"
						? <LapsedNote>{c.lapsedNote}</LapsedNote>
						: <StatusText $tone={presentation.tone}>{c.status[presentation.stage]}</StatusText>}

					{/* A lapsed owner gets no records and no setup controls — the presentation already decided
					    that, because "your plan will not serve this host" is a rule, not a rendering choice. */}
					{presentation.records.length > 0 && (
						<>
							<RecordsTitle>{c.recordsTitle}</RecordsTitle>
							<RecordList>
								{presentation.records.map((record) => (
									<DnsRecordCard key={record.kind} record={record}/>
								))}
							</RecordList>
							<Hint>{presentation.records.length > 1 ? c.recordsHint : c.recordsHintOne}</Hint>
						</>
					)}

					{error && <ErrorText role="alert">{error}</ErrorText>}

					{(presentation.canCheckDns || presentation.canRotateToken) && (
						<StageActions>
							{presentation.canCheckDns && (
								<SaveButton disabled={busy} onClick={() => void checkDns()}>{c.checkDns}</SaveButton>
							)}
							{presentation.canRotateToken && (
								<GhostButton disabled={busy} onClick={() => void rotateToken()}>{c.rotate}</GhostButton>
							)}
						</StageActions>
					)}

					<Actions>
						<DangerButton disabled={busy} onClick={openConfirm}>{c.remove}</DangerButton>
					</Actions>

					<ConfirmDialog
						open={confirmOpen}
						title={c.removeTitle}
						body={<ConfirmBody><strong>{domain.domain}</strong> — {c.removeBody}</ConfirmBody>}
						cancelLabel={m.common.cancel}
						confirmLabel={c.removeConfirm}
						onCancel={closeConfirm}
						onConfirm={() => void remove()}
					/>
				</CardMain>
			)}
		</>
	);
}

/**
 * One DNS record as a labelled name/value pair, each independently copyable.
 *
 * <p>Both halves get their own button because they are pasted into two different fields of a registrar's
 * form, and a single "copy the record" action would hand over a concatenation nothing accepts.
 */
function DnsRecordCard({record}: {record: CustomDomainRecord}) {
	const m = useMessages();
	const c = m.account.customDomain;
	return (
		<RecordCard>
			<RecordType>{record.type}</RecordType>
			<RecordField>
				<RecordCaption>{c.recordName}</RecordCaption>
				<CopyableValue value={record.name} copyAria={c.copyNameAria}/>
			</RecordField>
			<RecordField>
				<RecordCaption>{c.recordValue}</RecordCaption>
				<CopyableValue value={record.value} copyAria={c.copyValueAria}/>
			</RecordField>
		</RecordCard>
	);
}

/** One literal value with the shared "Copy / ✓ Copied" action. */
function CopyableValue({value, copyAria}: {value: string; copyAria: string}) {
	const m = useMessages();
	const {copied, copy} = useCopyFeedback(value);
	return (
		<RecordRow>
			<RecordValue>{value}</RecordValue>
			<ShareCopyButton type="button" $ok={copied} aria-label={copyAria} onClick={copy}>
				{copied ? `✓ ${m.common.copied}` : m.common.copy}
			</ShareCopyButton>
		</RecordRow>
	);
}
